import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { IDatabaseLineItem } from "../../types";
import { IXactimateToolRowData } from "../../../../../shared/common/Azure";
import { validateXactimateMappings, buildUnmappedPermutationGroups } from "../../../../../utils/validateXactimateMappings";
import { getAllValidPermutations } from "../../../../../utils/getAllValidPermutations";
import { findDuplicateMapping } from "../../../../../utils/findDuplicateMapping";
import { toastNotify } from "../../../../../shared/client/FrontendUtils";
import RestAPI from "../../../../../utils/restAPI";
import {
  fetchXactimateMappingsForLineItem,
  updateXactimateMapping,
  deleteXactimateMapping,
} from "../../../../../utils/xactimateMappingsAPI";
import TopMostPortal from "../../../../../components/ui/utils/Portals";
import { DuplicateMappingDialog, DuplicateMappingDialogData } from "../../../../../components/DuplicateMappingDialog";
import { getScopeTagDisplayName } from "../../../MappingTool/utils/ScopeTag";
import { Status, InvalidRowState, NewMappingFormState } from "./types";
import { ModalHeader } from "./ModalHeader";
import { ModalFooter } from "./ModalFooter";
import { InvalidMappingsSection } from "./InvalidMappingsSection";
import { UnmappedPermutationsSection } from "./UnmappedPermutationsSection";
import { MappingsSection } from "./MappingsSection";

interface XactimateSyncReviewModalProps {
  lineItem: IDatabaseLineItem;
  onDismiss: () => void;
  pendingNonDraftScopes?: string[];
  onPendingScopesApplied?: () => Promise<void>;
}

export const XactimateSyncReviewModal = ({
  lineItem,
  onDismiss,
  pendingNonDraftScopes,
  onPendingScopesApplied,
}: XactimateSyncReviewModalProps) => {
  const [status, setStatus] = useState<Status>("loading");
  const [goodMappings, setGoodMappings] = useState<IXactimateToolRowData[]>([]);
  const [invalidRows, setInvalidRows] = useState<InvalidRowState[]>([]);
  const [newMappingForms, setNewMappingForms] = useState<Map<string, NewMappingFormState>>(new Map());
  const [goodCollapsed, setGoodCollapsed] = useState(true);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());
  const [duplicateDialog, setDuplicateDialog] = useState<DuplicateMappingDialogData | null>(null);
  const hasPendingScopes = !!pendingNonDraftScopes && pendingNonDraftScopes.length > 0;
  const appliedPendingScopesRef = useRef(false);

  const validPermutations = useMemo(() => getAllValidPermutations(lineItem.elements), [lineItem.elements]);

  const unmappedGroups = useMemo(
    () => buildUnmappedPermutationGroups(lineItem, [...newMappingForms.keys()]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineItem.id, [...newMappingForms.keys()].join("\u0001")],
  );

  const revalidate = (allMappings: IXactimateToolRowData[], prevInvalidRows: InvalidRowState[]) => {
    const result = validateXactimateMappings(lineItem, allMappings);
    setGoodMappings(result.goodMappings);
    setExpandedGroupKeys(new Set());
    setInvalidRows(
      result.invalidMappings.map((m) => {
        const existing = prevInvalidRows.find((r) => r.mapping.id === m.id);
        return {
          mapping: m,
          selectedReplacement: existing?.selectedReplacement ?? validPermutations[0] ?? "",
          saving: false,
        };
      }),
    );
    setNewMappingForms((prev) => {
      const next = new Map<string, NewMappingFormState>();
      for (const perm of result.unmappedPermutations) {
        next.set(perm, { ...(prev.get(perm) ?? { expanded: false, cat: "", variable: "", activity: "", quantity: "" }), saving: false });
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchMappings = async () => {
      const mappings = await fetchXactimateMappingsForLineItem(lineItem.id);
      revalidate(mappings, []);
      setStatus("ready");
    };

    fetchMappings().catch(() => {
      toastNotify("Error loading Xactimate mappings", true);
      setStatus("error");
    });
  }, []);

  // In required mode, automatically apply the pending non-draft scopes once all
  // unmapped permutations have been resolved.
  useEffect(() => {
    if (status !== "ready") return;
    if (!hasPendingScopes || !onPendingScopesApplied) return;
    if (appliedPendingScopesRef.current) return;
    if (newMappingForms.size > 0) return;

    appliedPendingScopesRef.current = true;
    onPendingScopesApplied()
      .then(() => {
        toastNotify(
          `Applied scope${pendingNonDraftScopes!.length === 1 ? "" : "s"}: ${pendingNonDraftScopes!
            .map(getScopeTagDisplayName)
            .join(", ")}`,
        );
        onDismiss();
      })
      .catch(() => {
        appliedPendingScopesRef.current = false;
        toastNotify("Error applying scopes to line item", true);
      });
  }, [status, newMappingForms.size, hasPendingScopes]);

  const handleDismiss = () => {
    if (hasPendingScopes && newMappingForms.size > 0 && !appliedPendingScopesRef.current) {
      const scopeNames = pendingNonDraftScopes!.map(getScopeTagDisplayName).join(", ");
      const proceed = confirm(
        `${newMappingForms.size} permutation(s) are still unmapped. The line item will NOT be saved to: ${scopeNames}.\n\nClose anyway?`,
      );
      if (!proceed) return;
    }
    onDismiss();
  };

  // -----------------------------------------------------------------------
  // Handlers — invalid mappings
  // -----------------------------------------------------------------------

  const handleReplacementChange = (index: number, value: string) => {
    setInvalidRows((prev) => prev.map((r, i) => (i === index ? { ...r, selectedReplacement: value } : r)));
  };

  const handleUpdateInvalid = async (index: number) => {
    const row = invalidRows[index];
    const updated: IXactimateToolRowData = {
      ...row.mapping,
      acceptedLineString: row.selectedReplacement,
      lineNoSpaces: row.selectedReplacement.replace(/\s+/g, ""),
    };

    const allExisting = [...goodMappings, ...invalidRows.map((r) => r.mapping)];
    const duplicate = findDuplicateMapping(allExisting, updated);
    if (duplicate) {
      setDuplicateDialog({
        action: "update",
        selectedLineItemId: updated.selectedLineItemId,
        acceptedLineString: updated.acceptedLineString,
        existingCat: duplicate.cat,
        existingSel: duplicate.variable,
      });
      return;
    }

    setInvalidRows((prev) => prev.map((r, i) => (i === index ? { ...r, saving: true } : r)));

    try {
      await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${row.mapping.id}`, "POST", { mappingData: updated });
      toastNotify("Mapping updated");
      const allMappings = [...goodMappings, ...invalidRows.map((r, i) => (i === index ? updated : r.mapping))];
      revalidate(
        allMappings,
        invalidRows.filter((_, i) => i !== index),
      );
    } catch {
      toastNotify("Error updating mapping", true);
      setInvalidRows((prev) => prev.map((r, i) => (i === index ? { ...r, saving: false } : r)));
    }
  };

  const handleDeleteInvalid = async (index: number) => {
    const row = invalidRows[index];
    const yes = confirm("Delete this mapping?");
    if (!yes) return;

    try {
      await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${row.mapping.id}`, "DELETE");
      const remainingInvalidRows = invalidRows.filter((_, i) => i !== index);
      const allMappings = [...goodMappings, ...remainingInvalidRows.map((r) => r.mapping)];
      revalidate(allMappings, remainingInvalidRows);
    } catch {
      toastNotify("Error deleting mapping", true);
    }
  };

  // -----------------------------------------------------------------------
  // Handlers — valid mappings
  // -----------------------------------------------------------------------

  const handleUpdateValid = async (updated: IXactimateToolRowData) => {
    try {
      const success = await updateXactimateMapping(updated);
      if (!success) throw new Error("Update did not succeed");
      toastNotify("Mapping updated");
      const allMappings = [...goodMappings.map((m) => (m.id === updated.id ? updated : m)), ...invalidRows.map((r) => r.mapping)];
      revalidate(allMappings, invalidRows);
    } catch (err) {
      toastNotify("Error updating mapping", true);
      throw err;
    }
  };

  const handleDeleteValid = async (id: string) => {
    try {
      const success = await deleteXactimateMapping(id);
      if (!success) throw new Error("Delete did not succeed");
      toastNotify("Mapping deleted");
      const allMappings = [...goodMappings.filter((m) => m.id !== id), ...invalidRows.map((r) => r.mapping)];
      revalidate(allMappings, invalidRows);
    } catch (err) {
      toastNotify("Error deleting mapping", true);
      throw err;
    }
  };

  const handleCreateValid = async (newMapping: IXactimateToolRowData) => {
    const allExisting = [...goodMappings, ...invalidRows.map((r) => r.mapping)];
    const duplicate = findDuplicateMapping(allExisting, newMapping);
    if (duplicate) {
      setDuplicateDialog({
        action: "create",
        selectedLineItemId: newMapping.selectedLineItemId,
        acceptedLineString: newMapping.acceptedLineString,
        existingCat: duplicate.cat,
        existingSel: duplicate.variable,
      });
      throw new Error("duplicate");
    }
    try {
      await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${newMapping.id}`, "POST", { mappingData: newMapping });
      toastNotify("Mapping created");
      const allMappings = [...goodMappings, newMapping, ...invalidRows.map((r) => r.mapping)];
      revalidate(allMappings, invalidRows);
    } catch (err) {
      toastNotify("Error creating mapping", true);
      throw err;
    }
  };

  // -----------------------------------------------------------------------
  // Handlers — unmapped permutations
  // -----------------------------------------------------------------------

  const handleUpdateGroup = (perms: string[], patch: Partial<NewMappingFormState>) => {
    setNewMappingForms((prev) => {
      const next = new Map(prev);
      for (const perm of perms) {
        const existing = next.get(perm);
        if (existing) next.set(perm, { ...existing, ...patch });
      }
      return next;
    });
  };

  const handleToggleGroup = (representative: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(representative)) next.delete(representative);
      else next.add(representative);
      return next;
    });
  };

  const handleApplyToAll = (cat: string, variable: string, activity: string, quantity: string) => {
    setNewMappingForms((prev) => {
      const next = new Map(prev);
      for (const [perm, form] of prev) {
        next.set(perm, {
          ...form,
          ...(cat && { cat }),
          ...(variable && { variable }),
          ...(activity && { activity }),
          ...(quantity && { quantity }),
        });
      }
      return next;
    });
  };

  const handleSaveAll = async () => {
    // Collapsed groups save only the representative; expanded groups save each perm individually.
    const permsToSave: string[] = [];
    for (const group of unmappedGroups) {
      if (group.type === "individual") {
        permsToSave.push(group.perm);
      } else if (expandedGroupKeys.has(group.representative)) {
        permsToSave.push(...group.perms);
      } else {
        permsToSave.push(group.representative);
      }
    }

    const entries = permsToSave
      .map((perm) => [perm, newMappingForms.get(perm)!] as [string, NewMappingFormState])
      .filter(([, form]) => form && form.cat && form.variable && form.quantity);

    const newMappingsToCreate = entries.map(
      ([perm, form]) =>
        ({
          id: uuidv4(),
          createdAt: Date.now(),
          selectedLineItemId: lineItem.id,
          acceptedLineString: perm,
          lineNoSpaces: perm.replace(/\s+/g, ""),
          cat: form.cat || undefined,
          variable: form.variable || undefined,
          activity: form.activity || undefined,
          quantity: form.quantity || undefined,
        }) as IXactimateToolRowData,
    );

    const allExisting = [...goodMappings, ...invalidRows.map((r) => r.mapping)];
    let conflictExisting: IXactimateToolRowData | null = null;
    const conflict = newMappingsToCreate.find((nm) => {
      const existing = findDuplicateMapping(allExisting, nm);
      if (existing) {
        conflictExisting = existing;
        return true;
      }
      return false;
    });
    if (conflict) {
      setDuplicateDialog({
        action: "create",
        selectedLineItemId: conflict.selectedLineItemId,
        acceptedLineString: conflict.acceptedLineString,
        existingCat: conflictExisting?.cat,
        existingSel: conflictExisting?.variable,
      });
      return;
    }

    setNewMappingForms((prev) => {
      const next = new Map(prev);
      for (const perm of permsToSave) {
        const form = next.get(perm);
        if (form) next.set(perm, { ...form, saving: true });
      }
      return next;
    });

    try {
      await RestAPI.fetchRebuildAPI("/rest/tools/xactimateMappings/batch", "POST", { mappings: newMappingsToCreate });
      toastNotify(`${newMappingsToCreate.length} mapping(s) created`);
      const allMappings = [...goodMappings, ...invalidRows.map((r) => r.mapping), ...newMappingsToCreate];
      revalidate(allMappings, invalidRows);
    } catch {
      toastNotify("Error creating mappings", true);
      setNewMappingForms((prev) => {
        const next = new Map(prev);
        for (const perm of permsToSave) {
          const form = next.get(perm);
          if (form) next.set(perm, { ...form, saving: false });
        }
        return next;
      });
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const unmappedPermutations = [...newMappingForms.keys()];
  const hasIssues = invalidRows.length > 0 || unmappedPermutations.length > 0;

  return (
    <TopMostPortal>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
          <ModalHeader lineItem={lineItem} onDismiss={handleDismiss} />

          {hasPendingScopes && (
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
              <span className="font-semibold">Mapping required: </span>
              This line item won&apos;t be saved to{" "}
              <span className="font-medium">
                {pendingNonDraftScopes!.map(getScopeTagDisplayName).join(", ")}
              </span>{" "}
              until every permutation is mapped.
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {status === "loading" && <p className="text-sm text-stone-400 text-center py-8">Loading mappings…</p>}

            {status === "error" && (
              <p className="text-sm text-red-600 text-center py-8">
                Could not load Xactimate mappings.
                {hasPendingScopes && " The selected scope(s) were not applied — close and try saving again."}
              </p>
            )}

            {status === "ready" && !hasIssues && goodMappings.length === 0 && (
              <p className="text-sm text-stone-500 text-center py-8">No Xactimate mappings exist for this line item — nothing to sync.</p>
            )}

            {status === "ready" && !hasIssues && goodMappings.length > 0 && (
              <p className="text-sm text-green-700 text-center py-4 font-medium">
                All {goodMappings.length} mapping{goodMappings.length !== 1 ? "s" : ""} are valid — nothing to do.
              </p>
            )}

            {invalidRows.length > 0 && (
              <InvalidMappingsSection
                invalidRows={invalidRows}
                validPermutations={validPermutations}
                onReplacementChange={handleReplacementChange}
                onUpdate={handleUpdateInvalid}
                onDelete={handleDeleteInvalid}
              />
            )}

            {unmappedPermutations.length > 0 && (
              <UnmappedPermutationsSection
                newMappingForms={newMappingForms}
                unmappedGroups={unmappedGroups}
                expandedGroupKeys={expandedGroupKeys}
                onUpdateGroup={handleUpdateGroup}
                onToggleGroup={handleToggleGroup}
                onApplyToAll={handleApplyToAll}
                onSaveAll={handleSaveAll}
              />
            )}

            {goodMappings.length > 0 && (
              <MappingsSection
                lineItem={lineItem}
                mappings={goodMappings}
                collapsed={goodCollapsed}
                onToggle={() => setGoodCollapsed((v) => !v)}
                onUpdateMapping={handleUpdateValid}
                onDeleteMapping={handleDeleteValid}
                onCreateMapping={handleCreateValid}
              />
            )}
          </div>

          <ModalFooter onDismiss={handleDismiss} />
        </div>
      </div>
      {duplicateDialog && <DuplicateMappingDialog data={duplicateDialog} onClose={() => setDuplicateDialog(null)} />}
    </TopMostPortal>
  );
};
